import { useState, useMemo } from 'react';
import { saveEstimateSnapshot } from '@/hooks/useEstimateStore';

interface CheckItem {
  id: string;
  label: string;
  description: string;
  minPrice: number;
  maxPrice: number;
  unit: 'per_pyeong' | 'fixed';
}

interface CategoryGroup {
  category: string;
  items: CheckItem[];
}

interface ProcessGroup {
  id: string;
  name: string;
  icon: string;
  color: string;
  categories: CategoryGroup[];
}

const PROCESS_GROUPS: ProcessGroup[] = [
  {
    id: 'demolition',
    name: '철거 · 구조',
    icon: 'ri-hammer-line',
    color: '#8B7355',
    categories: [
      {
        category: '바닥재 · 벽체 철거',
        items: [
          { id: 'demo_wood_floor', label: '강마루 / 원목마루 철거', description: '접착제 제거 포함', minPrice: 3, maxPrice: 5, unit: 'per_pyeong' },
          { id: 'demo_vinyl', label: '장판 철거', description: '하지 상태에 따라 상이', minPrice: 1, maxPrice: 2, unit: 'per_pyeong' },
          { id: 'demo_tile_floor', label: '타일 바닥 철거', description: '두께·접착 방식에 따라 상이', minPrice: 5, maxPrice: 8, unit: 'per_pyeong' },
          { id: 'demo_wallpaper', label: '도배 제거 (합지/실크)', description: '초배지 포함', minPrice: 1, maxPrice: 3, unit: 'per_pyeong' },
          { id: 'demo_tile_wall', label: '타일 벽체 철거', description: '욕실·주방 벽 기준', minPrice: 6, maxPrice: 10, unit: 'per_pyeong' },
        ],
      },
      {
        category: '욕실 · 주방 · 구조 철거',
        items: [
          { id: 'demo_bath_s', label: '욕실 전체 철거 (소형)', description: '1.5평 기준, 도기·타일 포함', minPrice: 60, maxPrice: 100, unit: 'fixed' },
          { id: 'demo_bath_m', label: '욕실 전체 철거 (중형)', description: '2~3평 기준', minPrice: 100, maxPrice: 150, unit: 'fixed' },
          { id: 'demo_kitchen', label: '주방 싱크대 철거', description: '상·하부장 포함', minPrice: 30, maxPrice: 60, unit: 'fixed' },
          { id: 'demo_wall_light', label: '비내력벽 철거 (경량)', description: '석고보드 벽체 기준', minPrice: 30, maxPrice: 60, unit: 'fixed' },
          { id: 'demo_wall_brick', label: '비내력벽 철거 (조적)', description: '벽돌·블록 벽체 기준', minPrice: 60, maxPrice: 120, unit: 'fixed' },
        ],
      },
      {
        category: '창호 · 폐기물 처리',
        items: [
          { id: 'demo_window_s', label: '시스템창 철거 (소형)', description: '1.2m 이하 기준', minPrice: 10, maxPrice: 20, unit: 'fixed' },
          { id: 'demo_window_l', label: '시스템창 철거 (대형)', description: '1.2m 초과 기준', minPrice: 20, maxPrice: 40, unit: 'fixed' },
          { id: 'demo_front_door', label: '현관문 철거', description: '방화문 포함', minPrice: 20, maxPrice: 40, unit: 'fixed' },
          { id: 'demo_waste_30', label: '폐기물 처리 (30평)', description: '1톤 트럭 2~3대 기준', minPrice: 50, maxPrice: 80, unit: 'fixed' },
          { id: 'demo_elevator', label: '엘리베이터 보양 설치', description: '공사 기간 전체 기준', minPrice: 10, maxPrice: 20, unit: 'fixed' },
        ],
      },
    ],
  },
  {
    id: 'plumbing',
    name: '설비 · 배관',
    icon: 'ri-drop-line',
    color: '#4A7C8E',
    categories: [
      {
        category: '보일러 · 난방',
        items: [
          { id: 'boiler_s', label: '콘덴싱 보일러 교체 (소형)', description: '13~16kW, 자재+시공 포함', minPrice: 80, maxPrice: 120, unit: 'fixed' },
          { id: 'boiler_l', label: '콘덴싱 보일러 교체 (대형)', description: '20kW 이상, 대형 평수용', minPrice: 130, maxPrice: 200, unit: 'fixed' },
          { id: 'heating_pipe', label: '난방 배관 전체 교체', description: 'PB관 기준', minPrice: 8, maxPrice: 14, unit: 'per_pyeong' },
          { id: 'distributor', label: '온수 분배기 교체', description: '방별 밸브 포함', minPrice: 30, maxPrice: 60, unit: 'fixed' },
        ],
      },
      {
        category: '급수 · 배수 배관',
        items: [
          { id: 'water_pipe', label: '급수 배관 전체 교체', description: 'PB관 기준, 욕실·주방 포함', minPrice: 5, maxPrice: 10, unit: 'per_pyeong' },
          { id: 'drain_bath', label: '배수 배관 교체 (욕실)', description: '욕실 1개소 기준', minPrice: 30, maxPrice: 70, unit: 'fixed' },
          { id: 'drain_kitchen', label: '주방 배수 라인 교체', description: '싱크대 하부 포함', minPrice: 20, maxPrice: 40, unit: 'fixed' },
          { id: 'drain_laundry', label: '세탁기 배수 라인 추가', description: '신규 라인 설치', minPrice: 15, maxPrice: 30, unit: 'fixed' },
        ],
      },
      {
        category: '방수 공사',
        items: [
          { id: 'waterproof_bath_s', label: '욕실 방수 (소형)', description: '1.5평 기준, 우레탄 2중 방수', minPrice: 40, maxPrice: 70, unit: 'fixed' },
          { id: 'waterproof_bath_m', label: '욕실 방수 (중형)', description: '2~3평 기준', minPrice: 70, maxPrice: 110, unit: 'fixed' },
          { id: 'waterproof_balcony', label: '발코니 방수', description: '우레탄 방수 기준', minPrice: 30, maxPrice: 60, unit: 'fixed' },
        ],
      },
      {
        category: '위생기기 설치',
        items: [
          { id: 'toilet', label: '양변기 교체', description: '자재비 포함, 절수형', minPrice: 30, maxPrice: 70, unit: 'fixed' },
          { id: 'sink_set', label: '세면대 세트 교체', description: '수전·거울장 포함', minPrice: 40, maxPrice: 80, unit: 'fixed' },
          { id: 'shower_booth', label: '샤워 부스 설치', description: '프레임리스 강화유리 기준', minPrice: 50, maxPrice: 120, unit: 'fixed' },
          { id: 'faucet_bath', label: '욕실 수전 교체', description: '자재비 별도, 시공비', minPrice: 8, maxPrice: 20, unit: 'fixed' },
        ],
      },
    ],
  },
  {
    id: 'carpentry',
    name: '목공 · 창호',
    icon: 'ri-tools-line',
    color: '#6B8E6B',
    categories: [
      {
        category: '벽체 · 천장 목공',
        items: [
          { id: 'wall_new', label: '경량 벽체 신설', description: '석고보드 2겹 기준', minPrice: 8, maxPrice: 14, unit: 'per_pyeong' },
          { id: 'ceiling_gypsum', label: '천장 석고보드 시공', description: '경량 틀 포함', minPrice: 6, maxPrice: 10, unit: 'per_pyeong' },
          { id: 'insulation', label: '단열재 시공 (내벽)', description: '글라스울·압출법 선택', minPrice: 4, maxPrice: 8, unit: 'per_pyeong' },
          { id: 'soundproof', label: '방음재 시공', description: '층간·세대간 방음', minPrice: 6, maxPrice: 12, unit: 'per_pyeong' },
        ],
      },
      {
        category: '붙박이장 · 수납',
        items: [
          { id: 'closet_builtin', label: '붙박이장 (2400H 기준)', description: '폭 1.2m 기준, 맞춤 제작', minPrice: 100, maxPrice: 200, unit: 'fixed' },
          { id: 'dressroom', label: '드레스룸 전체 시공', description: '3~4평 기준', minPrice: 200, maxPrice: 400, unit: 'fixed' },
          { id: 'pantry', label: '팬트리 수납장', description: '주방 인접 수납 공간', minPrice: 80, maxPrice: 150, unit: 'fixed' },
          { id: 'shoerack', label: '신발장 맞춤 제작', description: '현관 맞춤 기준', minPrice: 60, maxPrice: 120, unit: 'fixed' },
        ],
      },
      {
        category: '시스템창 교체',
        items: [
          { id: 'window_double', label: '일반 이중창', description: '기본 단열 성능', minPrice: 40, maxPrice: 70, unit: 'fixed' },
          { id: 'window_lowe', label: '로이유리 이중창', description: '단열·자외선 차단 강화', minPrice: 60, maxPrice: 100, unit: 'fixed' },
          { id: 'window_triple', label: '방음 삼중창', description: '방음 최고 등급, 도로변 추천', minPrice: 100, maxPrice: 160, unit: 'fixed' },
          { id: 'window_balcony', label: '발코니 확장 창호', description: '대형 픽스창 포함', minPrice: 80, maxPrice: 140, unit: 'fixed' },
        ],
      },
      {
        category: '현관문 · 실내문',
        items: [
          { id: 'door_front', label: '현관 방화문 교체', description: '스마트 도어락 포함', minPrice: 120, maxPrice: 200, unit: 'fixed' },
          { id: 'door_interior', label: '실내문 세트 교체', description: '문틀·문짝·하드웨어 포함', minPrice: 25, maxPrice: 50, unit: 'fixed' },
          { id: 'door_sliding', label: '실내 중문 (슬라이딩)', description: '강화유리·우드 선택', minPrice: 60, maxPrice: 120, unit: 'fixed' },
          { id: 'door_pocket', label: '포켓도어 설치', description: '벽 매립형, 공간 절약', minPrice: 100, maxPrice: 180, unit: 'fixed' },
        ],
      },
    ],
  },
  {
    id: 'electric',
    name: '전기 · 조명',
    icon: 'ri-flashlight-line',
    color: '#B8860B',
    categories: [
      {
        category: '전기 배선 · 분전반',
        items: [
          { id: 'elec_full', label: '전기 배선 전체 교체', description: '분전반 포함, 30평 기준', minPrice: 8, maxPrice: 14, unit: 'per_pyeong' },
          { id: 'panel_20', label: '분전반 교체 (20회로)', description: '누전차단기 포함', minPrice: 30, maxPrice: 60, unit: 'fixed' },
          { id: 'panel_30', label: '분전반 교체 (30회로)', description: '대형 평수용', minPrice: 50, maxPrice: 90, unit: 'fixed' },
          { id: 'outlet_add', label: '콘센트 추가 설치', description: '배선 연장 포함', minPrice: 3, maxPrice: 6, unit: 'fixed' },
          { id: 'elec_upgrade', label: '전기 용량 증설', description: '한전 신청 포함', minPrice: 50, maxPrice: 120, unit: 'fixed' },
        ],
      },
      {
        category: '조명 기구 설치',
        items: [
          { id: 'light_downlight', label: '매입형 LED 다운라이트', description: '6인치 15W 기준, 시공비', minPrice: 2, maxPrice: 4, unit: 'fixed' },
          { id: 'light_cove', label: '간접 조명 (코브 조명)', description: 'LED 바 + 몰딩 포함', minPrice: 3, maxPrice: 6, unit: 'fixed' },
          { id: 'light_pendant', label: '펜던트 조명 설치', description: '배선 연결 시공비', minPrice: 3, maxPrice: 8, unit: 'fixed' },
          { id: 'light_kitchen', label: '주방 하부장 조명', description: 'LED 바 + 센서 포함', minPrice: 10, maxPrice: 20, unit: 'fixed' },
        ],
      },
      {
        category: '스위치 · 스마트홈',
        items: [
          { id: 'switch_touch', label: '터치 스위치 교체', description: '자재비 포함', minPrice: 3, maxPrice: 8, unit: 'fixed' },
          { id: 'switch_smart', label: '스마트 스위치 설치', description: '와이파이 연동형', minPrice: 5, maxPrice: 12, unit: 'fixed' },
          { id: 'smarthome_basic', label: '스마트홈 기본 패키지', description: '조명·난방 제어 포함', minPrice: 80, maxPrice: 150, unit: 'fixed' },
          { id: 'smarthome_full', label: '스마트홈 풀 패키지', description: '조명·난방·보안·가전 연동', minPrice: 150, maxPrice: 300, unit: 'fixed' },
          { id: 'wallpad', label: '월패드 교체', description: '인터폰·난방 통합 제어', minPrice: 30, maxPrice: 80, unit: 'fixed' },
        ],
      },
    ],
  },
  {
    id: 'tile',
    name: '타일 · 바닥',
    icon: 'ri-layout-bottom-line',
    color: '#6B5B45',
    categories: [
      {
        category: '욕실 타일',
        items: [
          { id: 'tile_bath_s', label: '욕실 타일 전체 시공 (소형)', description: '1.5평, 바닥+벽 포함', minPrice: 80, maxPrice: 130, unit: 'fixed' },
          { id: 'tile_bath_m', label: '욕실 타일 전체 시공 (중형)', description: '2~3평 기준', minPrice: 130, maxPrice: 200, unit: 'fixed' },
          { id: 'tile_porcelain', label: '포세린 타일 (600×1200)', description: '대형 타일 시공비 포함', minPrice: 15, maxPrice: 25, unit: 'per_pyeong' },
          { id: 'tile_mosaic', label: '모자이크 타일 포인트', description: '줄눈 포함', minPrice: 20, maxPrice: 35, unit: 'per_pyeong' },
        ],
      },
      {
        category: '주방 · 발코니 타일',
        items: [
          { id: 'tile_kitchen', label: '주방 뒷벽 타일 시공', description: '헤링본 패턴 포함', minPrice: 12, maxPrice: 20, unit: 'per_pyeong' },
          { id: 'tile_balcony', label: '발코니 타일 시공', description: '미끄럼 방지 타일 기준', minPrice: 10, maxPrice: 18, unit: 'per_pyeong' },
          { id: 'tile_entrance', label: '현관 타일 시공', description: '포세린·천연석 선택', minPrice: 12, maxPrice: 22, unit: 'per_pyeong' },
          { id: 'tile_grout', label: '타일 줄눈 재시공', description: '에폭시 줄눈 기준', minPrice: 3, maxPrice: 6, unit: 'per_pyeong' },
        ],
      },
      {
        category: '거실 · 방 바닥재',
        items: [
          { id: 'floor_wood', label: '강마루 시공', description: '자재비 포함, 오크 기준', minPrice: 10, maxPrice: 15, unit: 'per_pyeong' },
          { id: 'floor_hardwood', label: '원목마루 시공', description: '자재비 포함, 고급형', minPrice: 18, maxPrice: 30, unit: 'per_pyeong' },
          { id: 'floor_herringbone', label: '헤링본 마루 시공', description: '패턴 시공 추가비 포함', minPrice: 18, maxPrice: 28, unit: 'per_pyeong' },
          { id: 'floor_vinyl', label: '장판 시공', description: '방 전용, 자재비 포함', minPrice: 5, maxPrice: 8, unit: 'per_pyeong' },
          { id: 'floor_deco', label: '데코타일 시공', description: '사무실·다용도실 추천', minPrice: 6, maxPrice: 10, unit: 'per_pyeong' },
        ],
      },
    ],
  },
  {
    id: 'painting',
    name: '도장 · 도배',
    icon: 'ri-paint-brush-line',
    color: '#9B7B6B',
    categories: [
      {
        category: '도배 (벽지)',
        items: [
          { id: 'wallpaper_hanji', label: '합지 도배 (전체)', description: '자재비 포함, 30평 기준', minPrice: 4, maxPrice: 6, unit: 'per_pyeong' },
          { id: 'wallpaper_silk', label: '실크 도배 (전체)', description: '고급 실크 벽지 기준', minPrice: 7, maxPrice: 10, unit: 'per_pyeong' },
          { id: 'wallpaper_natural', label: '천연 소재 벽지', description: '황토·한지·삼베 등', minPrice: 12, maxPrice: 20, unit: 'per_pyeong' },
          { id: 'wallpaper_point', label: '포인트 벽지 시공', description: '1개 면 기준, 자재비 포함', minPrice: 15, maxPrice: 30, unit: 'fixed' },
        ],
      },
      {
        category: '페인트 도장',
        items: [
          { id: 'paint_wall', label: '벽 페인트 도장 (전체)', description: '친환경 수성 페인트', minPrice: 6, maxPrice: 9, unit: 'per_pyeong' },
          { id: 'paint_ceiling', label: '천장 페인트 도장', description: '화이트 무광 기준', minPrice: 4, maxPrice: 7, unit: 'per_pyeong' },
          { id: 'paint_molding', label: '문틀·몰딩 도장', description: '30평 전체 기준', minPrice: 20, maxPrice: 40, unit: 'fixed' },
          { id: 'paint_balcony', label: '외부 발코니 도장', description: '방수 페인트 기준', minPrice: 5, maxPrice: 9, unit: 'per_pyeong' },
        ],
      },
      {
        category: '고급 질감 마감',
        items: [
          { id: 'stucco', label: '스터코 마감', description: '유럽식 질감 마감', minPrice: 12, maxPrice: 18, unit: 'per_pyeong' },
          { id: 'microcement', label: '마이크로시멘트', description: '욕실·주방 적용 가능', minPrice: 20, maxPrice: 35, unit: 'per_pyeong' },
          { id: 'wood_panel', label: '우드 패널 시공', description: '포인트 벽 우드 마감', minPrice: 15, maxPrice: 28, unit: 'per_pyeong' },
          { id: 'putty', label: '퍼티 작업 (하지)', description: '도배·도장 전 하지 처리', minPrice: 2, maxPrice: 4, unit: 'per_pyeong' },
        ],
      },
    ],
  },
  {
    id: 'furniture',
    name: '가구 · 마감',
    icon: 'ri-sparkling-line',
    color: '#7A6E5F',
    categories: [
      {
        category: '주방 가구',
        items: [
          { id: 'kitchen_straight', label: '싱크대 전체 교체 (일자형)', description: '2400mm 기준, 상판 포함', minPrice: 200, maxPrice: 350, unit: 'fixed' },
          { id: 'kitchen_l', label: '싱크대 전체 교체 (ㄱ자형)', description: '2700mm 이상 기준', minPrice: 300, maxPrice: 500, unit: 'fixed' },
          { id: 'kitchen_top_ceramic', label: '상판만 교체 (세라믹)', description: '기존 하부장 유지', minPrice: 80, maxPrice: 150, unit: 'fixed' },
          { id: 'kitchen_top_quartz', label: '상판만 교체 (석영석)', description: '기존 하부장 유지', minPrice: 60, maxPrice: 120, unit: 'fixed' },
          { id: 'kitchen_faucet', label: '주방 수전 교체', description: '자재비 별도, 시공비', minPrice: 10, maxPrice: 25, unit: 'fixed' },
        ],
      },
      {
        category: '욕실 가구 · 위생기기',
        items: [
          { id: 'bath_vanity', label: '욕실 세면대 세트', description: '수전·거울장 포함', minPrice: 40, maxPrice: 80, unit: 'fixed' },
          { id: 'bath_toilet', label: '양변기 교체', description: '자재비 포함, 절수형', minPrice: 30, maxPrice: 70, unit: 'fixed' },
          { id: 'bath_mirror', label: '욕실 수납장 (거울장)', description: '매립형·부착형 선택', minPrice: 20, maxPrice: 50, unit: 'fixed' },
          { id: 'bath_acc', label: '욕실 액세서리 세트', description: '수건걸이·휴지걸이 등', minPrice: 10, maxPrice: 25, unit: 'fixed' },
        ],
      },
      {
        category: '마감 · 청소',
        items: [
          { id: 'clean_30', label: '입주 청소 (30평)', description: '전문 업체 기준', minPrice: 30, maxPrice: 60, unit: 'fixed' },
          { id: 'clean_40', label: '입주 청소 (40평 이상)', description: '전문 업체 기준', minPrice: 50, maxPrice: 90, unit: 'fixed' },
          { id: 'molding_all', label: '몰딩 마감 전체', description: '걸레받이·천장 몰딩 포함', minPrice: 2, maxPrice: 4, unit: 'per_pyeong' },
          { id: 'caulking', label: '실리콘 코킹 마감', description: '욕실·주방·창호 전체', minPrice: 10, maxPrice: 25, unit: 'fixed' },
        ],
      },
    ],
  },
];

const SIZE_OPTIONS = [10, 15, 20, 25, 30, 33, 40, 45, 50, 60];

export default function EstimateCalculator() {
  const [pyeong, setPyeong] = useState<number>(30);
  const [customPyeong, setCustomPyeong] = useState<string>('');
  const [useCustom, setUseCustom] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [expandedGroup, setExpandedGroup] = useState<string | null>('demolition');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['바닥재 · 벽체 철거']));

  const effectivePyeong = useCustom ? (parseInt(customPyeong) || 0) : pyeong;

  const toggleItem = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleGroupClick = (groupId: string, firstCategory: string) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(groupId);
      setExpandedCategories(new Set([firstCategory]));
    }
  };

  const { totalMin, totalMax, breakdown } = useMemo(() => {
    let min = 0;
    let max = 0;
    const breakdown: { group: string; color: string; items: { label: string; minP: number; maxP: number }[] }[] = [];

    PROCESS_GROUPS.forEach((group) => {
      const groupItems: { label: string; minP: number; maxP: number }[] = [];
      group.categories.forEach((cat) => {
        cat.items.forEach((item) => {
          if (checked.has(item.id)) {
            const minP = item.unit === 'per_pyeong' ? item.minPrice * effectivePyeong : item.minPrice;
            const maxP = item.unit === 'per_pyeong' ? item.maxPrice * effectivePyeong : item.maxPrice;
            min += minP;
            max += maxP;
            groupItems.push({ label: item.label, minP, maxP });
          }
        });
      });
      if (groupItems.length > 0) {
        breakdown.push({ group: group.name, color: group.color, items: groupItems });
      }
    });

    return { totalMin: min, totalMax: max, breakdown };
  }, [checked, effectivePyeong]);

  const checkedCount = checked.size;

  const getGroupCheckedCount = (group: ProcessGroup) =>
    group.categories.reduce((acc, cat) => acc + cat.items.filter((i) => checked.has(i.id)).length, 0);

  const handleGoToForm = () => {
    const selectedItems: { groupName: string; itemLabel: string; price: number }[] = [];
    PROCESS_GROUPS.forEach((group) => {
      group.categories.forEach((cat) => {
        cat.items.forEach((item) => {
          if (checked.has(item.id)) {
            const price = item.unit === 'per_pyeong'
              ? Math.round((item.minPrice + item.maxPrice) / 2) * effectivePyeong
              : Math.round((item.minPrice + item.maxPrice) / 2);
            selectedItems.push({ groupName: group.name, itemLabel: item.label, price });
          }
        });
      });
    });

    saveEstimateSnapshot({ pyeong: effectivePyeong, selectedItems, totalMin, totalMax });

    const formEl = document.getElementById('estimate-form');
    if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="py-20 bg-[#f8f6f1]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Calculator</span>
          <h2 className="text-3xl font-semibold text-[#1a1a1a] mt-2" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            공정별 견적 계산기
          </h2>
          <p className="text-[#666] text-sm mt-3">
            원하는 공정 항목을 선택하면 예상 견적이 실시간으로 계산됩니다.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left: Checklist */}
          <div className="flex-1 min-w-0">
            {/* Pyeong Selector */}
            <div className="bg-white rounded-2xl border border-[#e8e4dc] p-6 mb-6">
              <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-ruler-line text-[#c9a84c]" />
                </div>
                시공 면적 선택
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {SIZE_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setPyeong(s); setUseCustom(false); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                      !useCustom && pyeong === s
                        ? 'bg-[#c9a84c] text-[#0d0d0d]'
                        : 'border border-[#ddd] text-[#555] hover:border-[#c9a84c]'
                    }`}
                  >
                    {s}평
                  </button>
                ))}
                <button
                  onClick={() => setUseCustom(true)}
                  className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                    useCustom ? 'bg-[#c9a84c] text-[#0d0d0d]' : 'border border-[#ddd] text-[#555] hover:border-[#c9a84c]'
                  }`}
                >
                  직접 입력
                </button>
              </div>
              {useCustom && (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={customPyeong}
                    onChange={(e) => setCustomPyeong(e.target.value)}
                    placeholder="평수 입력"
                    min={1}
                    max={200}
                    className="w-36 border border-[#ddd] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c]"
                  />
                  <span className="text-sm text-[#666]">평</span>
                </div>
              )}
              <p className="text-xs text-[#999] mt-3">
                ※ 현재 선택: <strong className="text-[#c9a84c]">{effectivePyeong}평</strong> 기준으로 계산됩니다.
              </p>
            </div>

            {/* Process Groups */}
            <div className="space-y-3">
              {PROCESS_GROUPS.map((group) => {
                const groupCheckedCount = getGroupCheckedCount(group);
                const isExpanded = expandedGroup === group.id;

                return (
                  <div key={group.id} className="bg-white rounded-2xl border border-[#e8e4dc] overflow-hidden">
                    {/* Group Header */}
                    <button
                      onClick={() => handleGroupClick(group.id, group.categories[0].category)}
                      className="w-full flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-[#faf8f4] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: `${group.color}15` }}>
                          <i className={`${group.icon} text-base`} style={{ color: group.color }} />
                        </div>
                        <span className="text-sm font-semibold text-[#1a1a1a]">{group.name}</span>
                        {groupCheckedCount > 0 && (
                          <span className="bg-[#c9a84c] text-[#0d0d0d] text-xs font-bold px-2 py-0.5 rounded-full">
                            {groupCheckedCount}개 선택
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#bbb]">
                          {group.categories.reduce((a, c) => a + c.items.length, 0)}개 항목
                        </span>
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`ri-arrow-down-s-line text-[#999] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                    </button>

                    {/* Categories */}
                    {isExpanded && (
                      <div className="border-t border-[#f0ece4]">
                        {group.categories.map((cat) => {
                          const catKey = `${group.id}-${cat.category}`;
                          const isCatExpanded = expandedCategories.has(cat.category);
                          const catChecked = cat.items.filter((i) => checked.has(i.id)).length;

                          return (
                            <div key={catKey} className="border-b border-[#f5f2ec] last:border-b-0">
                              {/* Category Header */}
                              <button
                                onClick={() => toggleCategory(cat.category)}
                                className="w-full flex items-center justify-between px-6 py-3 bg-[#faf8f4] hover:bg-[#f5f1ea] transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: group.color }}
                                  />
                                  <span className="text-xs font-semibold text-[#555]">{cat.category}</span>
                                  {catChecked > 0 && (
                                    <span
                                      className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                                      style={{ backgroundColor: `${group.color}20`, color: group.color }}
                                    >
                                      {catChecked}
                                    </span>
                                  )}
                                </div>
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className={`ri-arrow-down-s-line text-[#bbb] text-sm transition-transform duration-200 ${isCatExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </button>

                              {/* Items */}
                              {isCatExpanded && (
                                <div className="divide-y divide-[#f5f2ec]">
                                  {cat.items.map((item) => {
                                    const isChecked = checked.has(item.id);
                                    const minP = item.unit === 'per_pyeong' ? item.minPrice * effectivePyeong : item.minPrice;
                                    const maxP = item.unit === 'per_pyeong' ? item.maxPrice * effectivePyeong : item.maxPrice;

                                    return (
                                      <label
                                        key={item.id}
                                        className={`flex items-start gap-4 px-6 py-3.5 cursor-pointer transition-colors ${isChecked ? 'bg-[#fdf9f0]' : 'hover:bg-[#faf8f4]'}`}
                                      >
                                        <div className="mt-0.5 flex-shrink-0">
                                          <div
                                            onClick={() => toggleItem(item.id)}
                                            className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all cursor-pointer ${
                                              isChecked ? 'border-[#c9a84c]' : 'border-[#ccc]'
                                            }`}
                                            style={{ backgroundColor: isChecked ? '#c9a84c' : 'transparent' }}
                                          >
                                            {isChecked && <i className="ri-check-line text-[#0d0d0d] text-xs" />}
                                          </div>
                                        </div>
                                        <div className="flex-1 min-w-0" onClick={() => toggleItem(item.id)}>
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                              <span className={`text-sm font-medium leading-snug ${isChecked ? 'text-[#1a1a1a]' : 'text-[#333]'}`}>
                                                {item.label}
                                              </span>
                                              <p className="text-xs text-[#aaa] mt-0.5">{item.description}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                              <span className={`text-sm font-bold whitespace-nowrap ${isChecked ? 'text-[#c9a84c]' : 'text-[#999]'}`}>
                                                {minP.toLocaleString()}~{maxP.toLocaleString()}
                                              </span>
                                              <p className="text-xs text-[#bbb] mt-0.5">
                                                만원{item.unit === 'per_pyeong' ? ` (${item.minPrice}~${item.maxPrice}/평)` : ''}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Summary Panel */}
          <div className="w-full lg:w-[380px] flex-shrink-0 lg:sticky lg:top-28">
            <div className="bg-[#2c2c2c] rounded-2xl p-7 text-white">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold" style={{ fontFamily: "'Noto Serif KR', serif" }}>예상 견적 요약</h3>
                {checkedCount > 0 && (
                  <button
                    onClick={() => setChecked(new Set())}
                    className="text-xs text-[#999] hover:text-white cursor-pointer transition-colors whitespace-nowrap"
                  >
                    전체 초기화
                  </button>
                )}
              </div>

              {/* Pyeong Info */}
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3 mb-5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-line text-[#c9a84c] text-sm" />
                </div>
                <span className="text-sm text-white/70">시공 면적</span>
                <span className="ml-auto text-sm font-semibold text-white">{effectivePyeong}평</span>
              </div>

              {/* Breakdown */}
              {breakdown.length > 0 ? (
                <div className="space-y-4 mb-6 max-h-72 overflow-y-auto pr-1">
                  {breakdown.map((b) => (
                    <div key={b.group}>
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                        <p className="text-xs font-semibold" style={{ color: b.color }}>{b.group}</p>
                      </div>
                      {b.items.map((item) => (
                        <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                          <span className="text-xs text-white/55 truncate mr-2 leading-snug">{item.label}</span>
                          <span className="text-xs text-white/80 whitespace-nowrap font-medium">
                            {item.minP.toLocaleString()}~{item.maxP.toLocaleString()}만
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-3">
                    <i className="ri-checkbox-multiple-line text-white/30 text-xl" />
                  </div>
                  <p className="text-sm text-white/40">공정을 선택하면<br />견적이 계산됩니다</p>
                </div>
              )}

              {/* Total */}
              <div className="border-t border-white/10 pt-5 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-white/60">선택 항목</span>
                  <span className="text-sm font-medium text-white">{checkedCount}개</span>
                </div>
                <div className="bg-white/5 rounded-xl px-4 py-4">
                  <p className="text-xs text-white/40 mb-2">예상 견적 범위</p>
                  {totalMin > 0 ? (
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-bold text-[#c9a84c]">{totalMin.toLocaleString()}</p>
                        <p className="text-xs text-white/40 mt-0.5">최소 (만원)</p>
                      </div>
                      <div className="text-white/30 text-lg font-light mx-2">~</div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white/80">{totalMax.toLocaleString()}</p>
                        <p className="text-xs text-white/40 mt-0.5">최대 (만원)</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-lg font-bold text-white/30 text-center py-2">-</p>
                  )}
                </div>
                <p className="text-xs text-white/30 mt-2 text-right">부가세 별도</p>
              </div>

              <div className="bg-white/5 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-white/40 leading-relaxed">
                  ※ 위 금액은 참고용 예상 견적입니다. 자재 등급, 현장 상황에 따라 달라질 수 있으며, 정확한 견적은 현장 방문 후 제공됩니다.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <a
                  href="/consultation"
                  className="block w-full bg-[#c9a84c] text-[#0d0d0d] text-sm font-bold py-3.5 rounded-full text-center cursor-pointer hover:bg-[#b8973b] transition-colors whitespace-nowrap"
                >
                  무료 상담 신청하기
                </a>
                <button
                  onClick={handleGoToForm}
                  disabled={checkedCount === 0}
                  className={`w-full text-sm font-medium py-3.5 rounded-full text-center transition-all whitespace-nowrap cursor-pointer ${
                    checkedCount > 0
                      ? 'border border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10'
                      : 'border border-white/10 text-white/30 cursor-not-allowed'
                  }`}
                >
                  {checkedCount > 0
                    ? `선택 ${checkedCount}개 · 신청서에 자동 입력`
                    : '공정을 선택하면 신청서에 자동 입력'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
